import { Component, OnInit, OnDestroy, HostListener, ViewChild, ElementRef } from '@angular/core';
import { Observable, Subscription } from 'rxjs';
import { ComponentCanDeactivate } from '../guards/save.guard';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ConfirmationDialog, ConfirmationDialogButtonDefinition, ConfirmationDialogResult } from '../components/confirmation-dialog/confirmation-dialog.component';
import { CommonService, UserService, CloudStorageService, ForgotPasswordService, NetworkMember, Company, CustomerService, SaveUserSetting, UserSetting, UserSettingFactory, UserSettingModel, GetAllSettings } from 'huddyapi';
import { Util } from '../components/util';
import { UUIDService } from '../services/uuid.service';
import { TextInputDialog, TextInputDialogResult } from '../components/text-input-dialog/text-input-dialog.component';
import { TempAuthService } from '../services/tempAuthService';
import moment_ from 'moment';
import { DesktopStorageService } from '../services/storage.service';
import { DesktopErrorHandler } from '../services/errorHandler.service';
import { Select, Store } from '@ngxs/store';
import { SelectOption } from '../components/multisave';
import { OnboardingDialog } from '../components/onboarding-dialog/onboarding-dialog.component';

declare global {
  interface Navigator {
      msSaveBlob?: (blob: any, defaultName?: string) => boolean
  }
}

const moment = moment_;

@Component({
  selector: 'app-profile',
  templateUrl: './profile.component.html',
  styleUrls: ['./profile.component.scss']
})
export class ProfileComponent implements OnInit, OnDestroy, ComponentCanDeactivate {
  @Select(state => state.userSettingState) userSettings$: Observable<UserSettingModel>;
  @ViewChild('name', { static: false }) set nameInput(nameInput: ElementRef) {
    this._nameInput = nameInput;
    if(this.shouldAutofocus && nameInput != null) {
      this.shouldAutofocus = false;
      nameInput.nativeElement.focus();
    }
  }

  get nameInput(): ElementRef {
    return this._nameInput;
  }

  tabIndex: number;
  first: string;
  last: string;
  homePref: string;
  homePrefOpts: SelectOption[];
  photoURL: string;
  password1: string;
  password2: string;
  readingImage: boolean;
  saving: Promise<boolean>;
  _nameInput: ElementRef;
  shouldAutofocus: boolean;
  user: any;
  userToView: any;
  userSubscription: any; //For some reason it doesn't like this being a Subscription
  settingSubscription: Subscription;
  settingsLoading: boolean;
  uuid: string;
  passwordShowing: boolean;
  companies: Company[];
  settings: UserSetting[];
  savedHomePref: string;

  constructor(
    private commonService: CommonService,
    private customerService: CustomerService,
    private store: Store,
    private uuidService: UUIDService,
    private forgotPassword: ForgotPasswordService,
    private storageService: DesktopStorageService,
    private authService: TempAuthService,
    private errorHandler: DesktopErrorHandler,
    public userService: UserService,
    public cloudStorageService: CloudStorageService,
    public dialog: MatDialog) {
    this.saving = null;
    this.settingsLoading = true;
    this.settings = [];
    this.readingImage = false;
    this.shouldAutofocus = true;
    this.passwordShowing = false;
    this.tabIndex = 0;
    this.homePrefOpts = [
      { value: 'compasses', displayValue: 'Creating compasses' },
      { value: 'library', displayValue: 'Assigning/monitoring compasses' }
    ];
    this.uuidService.getUUID().then((uuid: string) => {
      this.uuid = uuid;
    });
  }

  ngOnInit() {
    this.settingsLoading = true;
    this.userSubscription = this.commonService.getUserSubject().subscribe((user: any) => {
      if(user) {
        const member = NetworkMember.convertUser(user);
        const trustors: any[] = (user.whyUser && user.whyUser.trustors) ? user.whyUser.trustors : [];
      }
      this.user = user;
      if(this.user && this.userToView && this.user.login == this.userToView.login) {
        this.userToView = this.user;
        this.resetFields(this.user);
      }
    });
    this.commonService.getCurrentUserToViewSubject().subscribe((login: string) => {
      if(login) {
        if(this.user && this.user.login == login) {
          this.userToView = this.user;
          this.getCompaniesForUser();
          this.resetFields(this.user);
        } else {
          this.userToView = null;
          this.userService.getUser(login).subscribe((user: any) => {
            if(login == this.commonService.getCurrentUserToView()) {
              this.userToView = user;
              this.getCompaniesForUser();
              this.resetFields(user);
            }
          }, (error: any) => {
            this.errorHandler.handle(error, 'user');
          });
        }
      } else
        this.userToView = null;
    });
    this.settingSubscription = this.userSettings$.subscribe((state: UserSettingModel) => {
      this.settings = state.userSettings;
      const homePrefSetting: UserSetting = this.settings.find((setting: UserSetting): boolean => setting.settingName == OnboardingDialog.HOME_PREFERENCE_SETTING_NAME);
      this.savedHomePref = homePrefSetting?.userValue?.length > 0 ? homePrefSetting.userValue : null;
    });
    this.store.dispatch(new GetAllSettings()).subscribe(() => {
      this.homePref = this.savedHomePref;
      this.settingsLoading = false;
    }, (error: any) => {
      this.settingsLoading = false;
      this.errorHandler.handle(error, 'settings');
    });
  }

  ngOnDestroy() {
    if(this.userSubscription && this.userSubscription.unsubscribe)
      this.userSubscription.unsubscribe();
    if(this.settingSubscription)
      this.settingSubscription.unsubscribe();
  }

  canDeactivate(): boolean | Observable<boolean> | Promise<boolean> {
    if(!this.isSaving() && !this.isDirty())
      return true;
    return new Promise<boolean>((accept, reject) => {
      const isValid: boolean = this.isValid();
      let buttons: ConfirmationDialogButtonDefinition[] = [
        {
          text: 'Cancel',
          class: 'secondary',
          handler: (): void => {
            accept(false);
          }
        },
        {
          text: 'Discard changes',
          class: isValid ? 'danger' : '',
          handler: (): void => {
            accept(true);
          }
        }
      ];
      if(isValid) {
        buttons.push({
          text: 'Save changes',
          class: '',
          handler: (): Promise<void> => {
            return this.save().then((result: boolean) => {
              accept(result);
            }, (error: any) => {
              reject(error);
            });
          }
        });
      }
      const dialogRef = this.dialog.open(ConfirmationDialog, {
        width: '636px',
        data: {
          minWidth: isValid ? '300px' : '183px',
          title: 'Leave without saving?',
          body: 'Any unsaved changes will be lost forever.',
          buttons: buttons,
          init: (): Promise<boolean> => {
            return Promise.resolve(this.saving || true).then((result: boolean) => {
              if(!result) {
                accept(false);
                return false;
              }
              if(this.isDirty())
                return true;
              accept(true);
              return false;
            }, (error: any) => {
              reject(error);
              return false;
            });
          }
        }
      });
      dialogRef.beforeClosed().subscribe((result: ConfirmationDialogResult) => {
        if(!result)
          accept(false);
      });
    });
  }

  isLoading(): boolean {
    return !this.userToView || this.settingsLoading;
  }

  setTab(tabIndex: number) {
    this.tabIndex = tabIndex;
  }

  getTab(): number {
    if(!this.isMainUser() && this.tabIndex != 0)
      this.setTab(0);
    return this.tabIndex;
  }

  togglePasswordShowing() {
    this.passwordShowing = !this.passwordShowing;
  }

  resetFields(user: any) {
    this.first = user ? user.firstName : null;
    this.last = user ? user.lastName : null;
    this.homePref = this.savedHomePref;
    this.password1 = null;
    this.password2 = null;
    this.photoURL = user ? user.photoURL : null;;
  }

  getName(user: any): string {
    return Util.getName(user);
  }

  fileInputChanged(event: any) {
    this.fileAdded(event.target.files);
    event.target.value = '';
  }

  fileAdded(fileList: FileList) {
    if(this.readingImage || !fileList || fileList.length == 0)
      return;
    this.readingImage = true;
    const file: File = fileList.item(0);
    if(!(file.type && file.type.match(/^image\/(jpeg|jpg|png|gif)$/))) {
      this.readingImage = false;
      return;
    }
    let reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      this.readingImage = false;
      this.photoURL = reader.result.toString();
    };
  }

  getPhotoSaveName(user: any): string {
    return moment().format("YYYY-MM-DD[T]hh:mm:ss.SSS[Z]" + user.id.toString());
  }

  passwordCharacterCountGood(): boolean {
    return this.password1 && this.password1.length > 7;
  }

  passwordCapitalizationGood(): boolean {
    return this.password1 && this.password1.match(/[a-z]/) != null && this.password1.match(/[A-Z]/) != null;
  }

  passwordNumberGood(): boolean {
    return this.password1 && this.password1.match(/[0-9]/) != null;
  }

  passwordSpecialCharacterGood(): boolean {
    return this.password1 && this.password1.match(/[\`~!@#$%^&*()\-=_+[\]\\{}|;:'",.\/<>?]/) != null;
  }

  passwordsMatch(): boolean {
    return this.password1 && this.password1 == this.password2;
  }

  canShowEmail(): boolean {
    const emailToView: string = this.commonService.getCurrentUserToView();
    return !(emailToView && emailToView.endsWith('journalfor@localhost'));
  }

  isMainUser(): boolean {
    return this.user && this.user.login == this.commonService.getCurrentUserToView();
  }

  isSaving(): boolean {
    return this.saving != null;
  }

  isDirty(): boolean {
    return !this.isLoading() &&
      ((this.first || '') != (this.userToView.firstName || '')
      || (this.last || '') != (this.userToView.lastName || '')
      || this.homePref != this.savedHomePref
      || (this.password1 && this.password1.length > 0)
      || (this.password2 && this.password2.length > 0)
      || this.photoURL != this.userToView.photoURL);
  }

  isValid(): boolean {
    return (!this.first || this.first.length <= 255)
        && (!this.last || this.last.length <= 255)
        && (((!this.password1 || this.password1.length == 0) && (!this.password2 || this.password2.length == 0)) || (
          this.passwordCharacterCountGood()
          && this.passwordCapitalizationGood()
          && this.passwordNumberGood()
          && this.passwordSpecialCharacterGood()
          && this.passwordsMatch()
        ));
  }

  canRevert(): boolean {
    return this.userToView && !this.isSaving() && this.isDirty();
  }

  canSave(): boolean {
    return this.user && this.canRevert() && this.isValid();
  }

  canShowDelete(): boolean {
    return this.isMainUser();
  }

  canDelete(): boolean {
    return !this.isSaving() && this.canShowDelete();
  }

  revert() {
    if(!this.canRevert())
      return;
    this.resetFields(this.userToView);
  }

  getCompaniesForUser(): Promise<Company[]> {
    return this.customerService.getCompaniesForUser().toPromise().then((companies: Company[]) => {
      this.companies = companies;
      return companies;
    }, (error: any) => {
      this.errorHandler.handle(error, 'companies');
      throw error;
    });
  }

  canSaveCode(): boolean {
    return this.user && this.userToView && !this.settingsLoading && this.companies && !this.isSaving();
  }

  promptSaveCode() {
    if(!this.canSaveCode())
      return;
    this.dialog.open(TextInputDialog, {
      width: '636px',
      data: {
        minWidth: '235px',
        title: 'Enter new organization code',
        placeholder: 'Code',
        buttons: [
          {
            text: 'Cancel',
            class: 'secondary',
            handler: (): string => {
              return;
            }
          },
          {
            text: 'Save',
            class: '',
            handler: (value: string): Promise<string> => {
              return this.store.dispatch(new SaveUserSetting(UserSettingFactory.makeUserSetting(this.user.email, 'compass_code', value, value, 'STRING', 'COMPASS_SETTING'))).toPromise().then(async () => {
                return await this.getCompaniesForUser().then(() => '', () => '');
              }, (error: any) => {
                this.errorHandler.handleSimple(error, 'Error saving company code');
                return '';
              });
            }
          }
        ]
      }
    });
  }

  promptRemoveCompany(company: Company) {
    if(!(company && this.canSaveCode()))
      return;
    this.dialog.open(ConfirmationDialog, {
      width: '636px',
      data: {
        minWidth: '235px',
        title: 'Remove \'' + company.name + '\'?',
        body: 'Are you sure that you want to remove \'' + company.name + '\'?',
        buttons: [
          {
            text: 'Cancel',
            class: 'secondary',
            handler: (): void => {
              return;
            }
          },
          {
            text: 'Remove',
            class: '',
            handler: (): void | Promise<void> => {
              const compassCodeSettings: UserSetting[] = this.settings.filter((setting: UserSetting) => setting.settingName === 'compass_code');
              if(compassCodeSettings.length > 0) {
                let setting: UserSetting = UserSetting.convertObj(compassCodeSettings[0]);
                const codes: string[] = setting.userValue.split(',');
                const index: number = codes.indexOf(company.companyCode);
                if(index > -1) {
                  codes.splice(index, 1);
                  setting.userValue = codes.join(',');
                  setting.defaultValue = setting.userValue;
                  return this.store.dispatch(new SaveUserSetting(setting)).toPromise().then(async () => {
                    await this.getCompaniesForUser();
                  }, (error: any) => {
                    this.errorHandler.handleSimple(error, 'Error removing company');
                  });
                }
              }
            }
          }
        ]
      }
    });
  }

  save(): Promise<boolean> {
    if(!this.canSave())
      return null;
    let promise: Promise<boolean> = new Promise<boolean>(async (accept) => {
      let password: string = this.password1;
      if(password != null && password.length == 0)
        password = null;
      let user: any = {...this.userToView};
      user.firstName = (this.first && this.first.length > 0) ? this.first : null;
      user.lastName = (this.last && this.last.length > 0) ? this.last : null;
      const savingURL: string = this.photoURL;
      const savingHomePref: string = this.homePref;
      let shouldReturn: boolean = false;
      if(this.isMainUser() && password != null) {
        await this.forgotPassword.resetPassword(user.email).toPromise().catch((error: any) => {
          this.errorHandler.handle(error, 'password change', DesktopErrorHandler.COMMON_MESSAGES);
          shouldReturn = true;
          this.saving = null;
          accept(false);
        });
        if(shouldReturn)
          return;
        await new Promise<boolean>((innerAccept) => {
          let succeeded: boolean = false;
          const dialog: MatDialogRef<TextInputDialog> = this.dialog.open(TextInputDialog, {
            width: '636px',
            data: {
              minWidth: '251px',
              title: 'Enter code',
              body: 'An email was sent to ' + user.email + ' containing a code.  Enter that code below.',
              placeholder: 'Code',
              number: true,
              buttons: [
                {
                  text: 'Cancel',
                  class: 'secondary',
                  handler: (value: string): string => {
                    return;
                  }
                },
                {
                  text: 'Send new code',
                  class: 'secondary',
                  keepOpen: true,
                  handler: (value: string): Promise<string> => {
                    return new Promise<string>((a) => {
                      this.authService.getNewVerificationCode({
                        username: user.email,
                        resetType: 'reset'
                      }).subscribe(() => {
                        a('');
                      }, (error: any) => {
                        this.errorHandler.handleSimple(error, 'Error creating account');
                        a('Something went wrong');
                      });
                    });
                  }
                },
                {
                  text: 'Confirm',
                  class: '',
                  disableIfError: true,
                  handler: (value: string): Promise<string> => {
                    return new Promise<string>((a) => {
                      this.forgotPassword.finish({
                        username: user.email,
                        password: password,
                        verificationCode: value,
                        deviceUUID: this.uuid,
                        deviceToken: this.uuid
                      }).subscribe(() => {
                        succeeded = true;
                        a('');
                      }, (error: any) => {
                        this.errorHandler.handle(error, 'reset code', {
                          default: (): string => {
                            a('Reset failed');
                            return DesktopErrorHandler.MESSAGE_SILENT;
                          },
                          0: (): string => {
                            a('Reset failed');
                            return null;
                          },
                          406: (): string => {
                            a('Incorrect code');
                            return DesktopErrorHandler.MESSAGE_SILENT;
                          }
                        });
                      });
                    });
                  }
                }
              ]
            }
          });
          dialog.beforeClosed().subscribe((result: TextInputDialogResult) => {
            innerAccept(succeeded);
          });
        }).then((result: boolean) => {
          if(!result) {
            shouldReturn = true;
            this.saving = null;
            accept(false);
          }
        }, (error: any) => {
          this.errorHandler.handle(error, 'password reset', DesktopErrorHandler.COMMON_MESSAGES);
          shouldReturn = true;
          this.saving = null;
          accept(false);
        });
        if(shouldReturn)
          return;
      }
      if(savingURL != user.photoURL) {
        const isBase64: boolean = Util.isBase64Image(savingURL);
        if(user.photoURL != null || isBase64) {
          await this.cloudStorageService.saveImage((isBase64 ? this.getPhotoSaveName(user) : null), (isBase64 ? savingURL : null), user.photoURL).toPromise().then((url: string) => {
            user.photoURL = isBase64 ? url : savingURL;
          }, (error: any) => {
            this.errorHandler.handle(error, 'image');
            shouldReturn = true;
            this.saving = null;
            accept(false);
          });
          if(shouldReturn)
            return;
        } else
          user.photoURL == savingURL;
      }
      if(savingHomePref != this.savedHomePref) {
        await this.store.dispatch(new SaveUserSetting(UserSettingFactory.makeUserSetting(user.email, OnboardingDialog.HOME_PREFERENCE_SETTING_NAME, OnboardingDialog.HOME_PREFERENCE_DEFAULT_VALUE, savingHomePref, 'STRING', 'COMPASS_SETTING'))).toPromise().then(() => {
          this.savedHomePref = savingHomePref;
        }).catch((error: any) => {
          this.errorHandler.handle(error, 'inmage');
          shouldReturn = true;
          this.saving = null;
          accept(false);
        });
        if(shouldReturn)
          return;
      } else
        this.homePref = savingHomePref;
      this.userService.updateUser(user).subscribe((newUser: any) => {
        if(newUser.id == this.user.id)
          this.commonService.updateUser(newUser);
        else if(user.whyUser) {
          this.user.whyUser.trustors = (this.user.whyUser.trustors || []).map((trustor: any) => {
            if(trustor.trustorId == newUser.id) {
              trustor.userName = this.getName(newUser);
              trustor.photoURL = newUser.photoURL;
            }
            return trustor;
          });
          this.userToView = newUser;
          this.resetFields(newUser);
          CommonService.setUserAuth(CommonService.jwt, this.commonService.getCurrentUserToView(), this.user);
          this.commonService.getUserSubject().next(this.user);
        }
        accept(true);
        this.saving = null;
      }, (error: any) => {
        this.errorHandler.handle(error, 'user');
        accept(false);
        this.saving = null;
      });
    });
    this.saving = promise;
    return promise;
  }

  delete(): Promise<boolean> {
    return new Promise<boolean>(accept => {
      this.resetFields(this.userToView);
      this.userService.deleteAccount(false).subscribe(() => {
        this.authService.logOut().finally(() => {
          this.storageService.clear();
          accept(true);
        });
      }, (error: any) => {
        this.errorHandler.handle(error, 'account');
        accept(false);
      });
    });
  }

  promptDelete() {
    if(!this.canDelete())
      return;
    const member: NetworkMember = NetworkMember.convertUser(this.user);
    this.dialog.open(ConfirmationDialog, {
      width: '636px',
      data: {
        minWidth: '300px',
        title: 'Delete my account',
        body: 'Deleting will remove history, cookies, and other data associated with your account.  Are you sure that you want to delete your account (' + member.email + ')?',
        checkboxText: 'I agree to these terms',
        networkMember: member,
        buttons: [
          {
            text: 'Cancel',
            class: 'secondary',
            handler: (): boolean => {
              return false;
            }
          },
          {
            requiresCheckbox: true,
            text: 'Delete',
            class: '',
            handler: (): boolean | Promise<boolean> => {
              if(this.isSaving())
                return false;
              return this.delete();
            },
            disabled: (): boolean => {;
              return this.isSaving();
            }
          }
        ]
      }
    });
  }

  downloadAccount() {
    this.userService.downloadAccount().subscribe(response => {
      var csvData = new Blob([response], {type: 'text/csv;charset=utf-8;'});
      var csvURL =  null;
      if (navigator.msSaveBlob) {
          csvURL = navigator.msSaveBlob(csvData, 'userData.csv');
      } else {
          csvURL = window.URL.createObjectURL(csvData);
      }
      var tempLink = document.createElement('a');
      tempLink.href = csvURL;
      tempLink.setAttribute('download', 'userData.csv');
      tempLink.click();
    }, (error: any) => this.errorHandler.handleSimple(error, 'Error downloading account'));
  }

  @HostListener('window:keydown', ['$event'])
  handleKeydown(event: any) {
    if(event.key != 's' || (this.dialog.openDialogs && this.dialog.openDialogs.length > 0))
      return;
    if((window.navigator.platform.match("Mac") ? event.metaKey : event.ctrlKey)) {
      event.preventDefault();
      this.save();
    }
  }
}
